"use client"

import { useRef, useCallback } from "react"
import DisplayPrice from "@modules/common/components/display-price"
import { VariantPrice } from "types/global"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import WishlistButton from "@modules/common/components/wishlist-button"
import { getProductUrl } from "@lib/util/product"

type ProductData = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  /** Price OBJECT, so the display currency can be rendered (#166532). */
  price: VariantPrice | null
}

export default function RelatedProductsClient({ products }: { products: ProductData[] }) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = useCallback((direction: "left" | "right") => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({
      left: direction === "left" ? -300 : 300,
      behavior: "smooth",
    })
  }, [])

  if (!products.length) return null

  return (
    <section className="bg-[#f7f7f7] py-6 sm:py-10">
      <div className="max-w-screen-2xl mx-auto px-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl sm:text-3xl font-black text-[#143469]">
            Related Products
          </h2>
          <div className="hidden md:flex gap-2">
            <button
              type="button"
              onClick={() => scroll("left")}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:border-brand-navy hover:text-brand-navy transition-colors"
              aria-label="Scroll left"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              className="w-10 h-10 rounded-full border border-brand-navy flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
              aria-label="Scroll right"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2"
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="flex-none w-[170px] sm:w-[200px] md:w-[220px] snap-start bg-white rounded-lg p-3 sm:p-4"
            >
              <LocalizedClientLink
                href={getProductUrl(product.handle)}
                className="group block"
              >
                <div className="relative aspect-[3/4] mb-2 rounded overflow-hidden bg-gray-50">
                  {product.thumbnail ? (
                    <Image
                      src={product.thumbnail}
                      alt={product.title}
                      fill
                      className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                      sizes="220px"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-xs">
                      No image
                    </div>
                  )}
                  <WishlistButton productId={product.id} productTitle={product.title} />
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 mb-1 min-h-[32px]">
                  {product.title}
                </h3>
                {product.price && (
                  <p className="text-sm font-bold text-gray-900 mb-2">
                    <DisplayPrice
                      amount={product.price.calculated_price_number}
                      currencyCode={product.price.currency_code}
                      data-testid="price"
                    />
                  </p>
                )}
                <span className="block w-full py-2 text-xs sm:text-sm font-semibold text-center rounded-full bg-brand-hero-navy text-white group-hover:bg-brand-primary-blue transition-colors">
                  Add to cart
                </span>
              </LocalizedClientLink>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
